/**
 * Formulario `/justificar/$day?tipo=LATE|EARLY_LEAVE|ABSENCE` (008 · T111; contracts/ui.md §2.3,
 * contracts/staff-api.md S3, spec US7-2, US7-5 y US7-9).
 *
 * Pantalla completa sin pestañas (como `/regularizar/$markId` de la 007). Lee el día de
 * `GET /attendance/days` y **congela** esa foto al abrir: una recarga posterior (foco, invalidación
 * tras enviar) no desmonta el formulario ni la hoja de error. Si el día no está o el servidor ya no
 * permite ese tipo (`justify.kinds`), "Este día ya no se puede justificar".
 *
 * - `h1` "Justificar tardanza/salida anticipada/falta" y la card de resumen ("Lunes 22 · Turno
 *   08:00 – 17:00 · Tardanza · 12 min") con lo que ya evaluó el servidor.
 * - Motivo obligatorio con contador (`JUSTIFICATION_REASON_MAX`); vacío ⇒ "Cuéntanos qué pasó."
 *   sin llamar a la API; un `400` del servidor se pinta bajo el motivo.
 * - Clave de idempotencia por intento: nace al abrir, se **conserva** en los reintentos del mismo
 *   motivo (un reintento tras un fallo de red no duplica, US7-9) y se **renueva** si cambió.
 * - Sin optimistic UI. Éxito ⇒ `justify-success` y regreso a Asistencia a los 2,5 s o al tocar.
 *   `409 JUSTIFICATION_UNAVAILABLE`/`PERIOD_CLOSED` ⇒ hoja `justify-unavailable-sheet` con el texto
 *   del motivo y vuelta a Asistencia (las consultas ya se invalidaron en `useCreateJustification`).
 * - Sin red: "Sin conexión" deshabilitado.
 */
import { useNavigate, useRouter } from '@tanstack/react-router';
import { CalendarClock, CircleAlert } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';

import { JUSTIFICATION_REASON_MAX, type JustificationKind, type StaffDay } from '@fermin/contracts';

import { ApiError, NetworkError } from '../../api/client';
import { useOnlineStatus } from '../../api/online';
import { Button } from '../../components/ui/button';
import { GenericErrorScreen } from '../../components/ui/generic-error';
import { OfflineState } from '../../components/ui/offline-state';
import { Screen } from '../../components/ui/screen';
import { Sheet } from '../../components/ui/sheet';
import { Skeleton, SkeletonText } from '../../components/ui/skeleton';
import { Textarea } from '../../components/ui/textarea';
import { t } from '../../i18n';
import { useStaffDays } from '../schedule/api';
import { shiftLineOf, weekdayAndDay } from '../schedule/format';

import {
  isStaleJustificationError,
  newIdempotencyKey,
  reasonErrorOf,
  unavailableMessage,
  useCreateJustification,
} from './api';
import { capitalize, kindWithMinutes, observedMinutesOf } from './labels';

/** Regreso automático a Asistencia tras el éxito (mismo tiempo que la regularización). */
export const JUSTIFY_SUCCESS_RETURN_MS = 2_500;

function useGoToHistory(): () => void {
  const navigate = useNavigate();
  return () => {
    void navigate({ to: '/asistencia', replace: true });
  };
}

export function JustifyLoading() {
  return (
    <Screen data-testid="justify-loading">
      <SkeletonText label={t('common.states.loading')} className="gap-fm-4">
        <Skeleton radius="lg" className="h-20 w-full" />
        <Skeleton radius="lg" className="h-32 w-full" />
      </SkeletonText>
    </Screen>
  );
}

function JustifyGone() {
  const goToHistory = useGoToHistory();
  return (
    <Screen
      data-testid="justify-gone"
      align="center"
      footer={
        <Button variant="secondary" onClick={goToHistory}>
          {t('justification.gone.back')}
        </Button>
      }
    >
      <div className="flex flex-col items-center gap-fm-3 text-center">
        <span
          aria-hidden="true"
          className="flex h-16 w-16 items-center justify-center rounded-fm-pill bg-fm-info-soft text-fm-info-text"
        >
          <CalendarClock size={28} />
        </span>
        <h1 className="font-display text-fm-xl font-bold text-fm-text">
          {t('justification.gone.title')}
        </h1>
        <p className="text-fm-base text-fm-text-secondary">{t('justification.gone.description')}</p>
      </div>
    </Screen>
  );
}

function JustifySuccess() {
  const navigate = useNavigate();
  const goToHistory = useGoToHistory();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void navigate({ to: '/asistencia', replace: true });
    }, JUSTIFY_SUCCESS_RETURN_MS);
    return () => window.clearTimeout(timer);
  }, [navigate]);

  return (
    // Tocar cualquier parte adelanta el regreso; el botón del pie hace lo mismo con teclado.
    <div data-testid="justify-success" onClick={goToHistory}>
      <Screen
        align="center"
        footer={
          <Button variant="secondary" onClick={goToHistory}>
            {t('justification.success.back')}
          </Button>
        }
      >
        <div role="status" className="flex flex-col items-center gap-fm-3 text-center">
          <span
            aria-hidden="true"
            className="flex h-24 w-24 items-center justify-center rounded-fm-pill bg-fm-success-soft"
          >
            <svg viewBox="0 0 48 48" className="h-12 w-12">
              <path
                d="M12 25 L21 34 L37 16"
                fill="none"
                strokeWidth={5}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="stroke-fm-success-text"
              />
            </svg>
          </span>
          <h1 className="font-display text-fm-xl font-bold text-fm-text">
            {t('justification.success.title')}
          </h1>
          <p className="text-fm-base text-fm-text-secondary">
            {t('justification.success.description')}
          </p>
        </div>
      </Screen>
    </div>
  );
}

/** "Lunes 22 · Turno 08:00 – 17:00 · Tardanza · 12 min" (lo que ya evaluó el servidor). */
export function summaryOf(day: StaffDay, kind: JustificationKind): string {
  const parts = [capitalize(weekdayAndDay(day.day))];
  const shift = shiftLineOf(day);
  if (shift !== null) parts.push(shift);
  parts.push(kindWithMinutes(kind, observedMinutesOf(day, kind)));
  return parts.join(' · ');
}

interface JustifyFormProps {
  day: StaffDay;
  kind: JustificationKind;
  onSuccess: () => void;
}

function JustifyForm({ day, kind, onSuccess }: JustifyFormProps) {
  const online = useOnlineStatus();
  const router = useRouter();
  const goToHistory = useGoToHistory();
  const create = useCreateJustification();

  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | undefined>();
  const [formError, setFormError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  /** Clave del intento en curso y el motivo con el que se usó por última vez. */
  const attempt = useRef<{ key: string; reason: string | null }>({
    key: newIdempotencyKey(),
    reason: null,
  });

  const goBack = () => {
    if (router.history.canGoBack()) {
      router.history.back();
    } else {
      goToHistory();
    }
  };

  const handleError = (error: unknown) => {
    if (error instanceof NetworkError) {
      setFormError(t('justification.form.networkError'));
      return;
    }
    if (!(error instanceof ApiError)) {
      setFormError(t('justification.form.genericError'));
      return;
    }
    if (isStaleJustificationError(error)) {
      setUnavailable(unavailableMessage(error));
      return;
    }
    if (error.is('COMPANY_SUSPENDED')) {
      setUnavailable(t('justification.errors.COMPANY_SUSPENDED'));
      return;
    }
    const fieldError = reasonErrorOf(error);
    if (fieldError !== null) {
      setReasonError(fieldError);
      return;
    }
    if (error.is('RATE_LIMITED')) {
      setFormError(t('justification.form.rateLimited'));
      return;
    }
    if (error.is('CONFLICT')) {
      // `IDEMPOTENCY_KEY_REUSED`: el próximo envío sale con una clave nueva.
      attempt.current = { key: newIdempotencyKey(), reason: null };
    }
    setFormError(t('justification.form.genericError'));
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (create.isPending || !online) return;
    const trimmed = reason.trim();
    setFormError(null);
    if (trimmed === '') {
      setReasonError(t('justification.form.reasonRequired'));
      return;
    }
    setReasonError(undefined);

    if (attempt.current.reason !== null && attempt.current.reason !== trimmed) {
      attempt.current = { key: newIdempotencyKey(), reason: null };
    }
    attempt.current.reason = trimmed;

    create.mutate(
      { day: day.day, kind, reason: trimmed, idempotency_key: attempt.current.key },
      { onSuccess, onError: handleError },
    );
  };

  return (
    <form noValidate onSubmit={submit} className="contents">
      <Screen
        data-testid="justify-screen"
        title={t(`justification.form.title.${kind}`)}
        footer={
          <>
            <Button
              type="submit"
              data-testid="justify-submit"
              disabled={!online || create.isPending}
              loading={create.isPending}
              loadingText={t('justification.form.submitting')}
            >
              {online ? t('justification.form.submit') : t('justification.form.offline')}
            </Button>
            <Button type="button" variant="ghost" onClick={goBack} disabled={create.isPending}>
              {t('justification.form.cancel')}
            </Button>
          </>
        }
      >
        <section
          data-testid="justify-summary"
          data-kind={kind}
          className="flex flex-col gap-fm-1 rounded-fm-lg border border-fm-border bg-fm-surface p-fm-4"
        >
          <span className="text-fm-sm text-fm-text-secondary">
            {t('justification.form.summaryLabel')}
          </span>
          <span className="fm-tabular font-ui text-fm-base font-semibold text-fm-text">
            {summaryOf(day, kind)}
          </span>
        </section>

        <Textarea
          data-testid="justify-reason"
          counterTestId="justify-reason-counter"
          label={t('justification.form.reasonLabel')}
          placeholder={t('justification.form.reasonPlaceholder')}
          help={t('justification.form.reasonHelp')}
          value={reason}
          maxLength={JUSTIFICATION_REASON_MAX}
          error={reasonError}
          required
          rows={4}
          onChange={(event) => {
            setReason(event.target.value);
            setReasonError(undefined);
          }}
        />

        {formError !== null ? (
          <p
            data-testid="justify-error"
            role="alert"
            className="flex items-center gap-fm-2 rounded-fm-md bg-fm-danger-soft p-fm-3 text-fm-base text-fm-danger-text"
          >
            <CircleAlert size={20} aria-hidden="true" className="shrink-0" />
            {formError}
          </p>
        ) : null}
      </Screen>

      <Sheet
        open={unavailable !== null}
        onClose={goToHistory}
        title={t('justification.unavailableSheet.title')}
        description={unavailable ?? undefined}
        className="pb-fm-4"
        footer={
          <div data-testid="justify-unavailable-sheet" className="flex flex-col gap-fm-3">
            <Button variant="secondary" onClick={goToHistory}>
              {t('justification.unavailableSheet.back')}
            </Button>
          </div>
        }
      />
    </form>
  );
}

export interface JustifyScreenProps {
  day: string;
  /** `?tipo=` ya validado por la ruta; `undefined` si no vino o no es un tipo conocido. */
  kind: JustificationKind | undefined;
}

export function JustifyScreen({ day: dayKey, kind }: JustifyScreenProps) {
  const online = useOnlineStatus();
  const days = useStaffDays();
  const [done, setDone] = useState(false);
  /** Foto del día al abrir: las recargas posteriores no desmontan el formulario. */
  const [snapshot, setSnapshot] = useState<StaffDay | null>(null);

  const current = days.data?.days.find((entry) => entry.day === dayKey);
  const openable =
    current !== undefined && kind !== undefined && current.justify.kinds.includes(kind);

  // Estado derivado durante el render (patrón de React para "guardar el primer valor"): sin efecto.
  if (snapshot === null && openable) {
    setSnapshot(current);
  }

  if (done) {
    return <JustifySuccess />;
  }

  const day = snapshot ?? (openable ? current : null);
  if (day !== null && kind !== undefined) {
    return <JustifyForm day={day} kind={kind} onSuccess={() => setDone(true)} />;
  }

  if (days.data === undefined) {
    if (!online) {
      return (
        <Screen>
          <OfflineState onRetry={() => void days.refetch()} retrying={days.isFetching} />
        </Screen>
      );
    }
    if (days.isError) {
      return <GenericErrorScreen onRetry={() => void days.refetch()} />;
    }
    return <JustifyLoading />;
  }

  return <JustifyGone />;
}
